import { useOrgContext } from "@/components/providers/org-context-provider";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { SECTION_EYEBROW_TEXT_CLASS } from "@/components/premium";
import { profileFullName, resolveActiveProfile } from "@/lib/active-profile";
import { isPlatformRoute } from "@/lib/route-context";
import { ProfileOptionsList } from "./options-list";

interface OrgSwitcherProps {
  /**
   * When true, renders a 40px square icon-button trigger and portals the
   * dropdown to document.body. Used inside the collapsed sidebar where the
   * full-width dropdown would be clipped by the parent's overflow-hidden.
   */
  compact?: boolean;
  /**
   * When true (and not compact), the dropdown opens above the trigger instead
   * of below. Used inside fixed bottom drawers (e.g. the mobile More sheet)
   * where there is no room below the trigger.
   */
  openUpward?: boolean;
}

export function OrgSwitcher({ compact = false, openUpward = false }: OrgSwitcherProps) {
  const { memberships, activeOrgId, activeOrgName, username, switchToOrg, switchToPersonal, isLoading, isPlatformAdmin } =
    useOrgContext();
  const [location, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(t) &&
        triggerRef.current && !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  if (isLoading && compact) {
    return (
      <button
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-md border border-border-default bg-bg-elevated"
        disabled
        aria-label="Switch profile"
      >
        {/* A neutral pulse, NOT a building glyph. The old placeholder icon was
            the org glyph, so a personal-context user watched their profile
            appear to be an org until memberships resolved — the very confusion
            this component now exists to remove. Neutral grey = loading per
            CLAUDE.md Design Constraints. */}
        <Skeleton className="h-4 w-4 rounded-full" />
      </button>
    );
  }
  if (isLoading) {
    return (
      <button className="rounded-md px-3 py-2 text-sm text-text-tertiary" disabled>
        Loading…
      </button>
    );
  }

  const onPlatform = isPlatformRoute(location);

  // Shared with the app-shell header chip so the two indicators can never
  // disagree about which profile is active (client/src/lib/active-profile.ts).
  const active = resolveActiveProfile({ onPlatform, activeOrgId, activeOrgName, username });
  // Full form ("Personal (@ari_test)") for tooltips + accessible names; the
  // visible trigger splits it into a name line and a muted handle line.
  const label = profileFullName(active);
  const ActiveIcon = active.Icon;

  // Fold the caller's own personal node (node_kind='personal') into the single
  // "Personal" entry below — it is the caller's personal investing identity, not
  // a separate org, so listing it as its own row duplicates "Personal"
  // (#927 / spec AC-8).
  const orgMemberships = memberships.filter(
    (m) => m.nodeKind !== "personal",
  );

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
    setOpen((o) => !o);
  };

  const optionsList = (
    <ProfileOptionsList
      orgMemberships={orgMemberships}
      activeOrgId={activeOrgId}
      onPlatform={onPlatform}
      isPlatformAdmin={isPlatformAdmin}
      onPickPersonal={() => {
        // switchToPersonal() updates context optimistically (synchronously,
        // before its audit-log POST resolves) — closing the dropdown and
        // navigating here, rather than awaiting it first, is what actually
        // shows the new tabs the instant the user picks Personal instead of
        // after a network round trip (console transition tab lag, #1965).
        // A rejected switch (rare — stale membership) rolls its own state
        // back; `.catch` here just avoids an unhandled rejection.
        void switchToPersonal().catch(() => {});
        // Leaving Platform Console needs an explicit navigation: the nav
        // item lists in sidebar.tsx/bottom-tabs.tsx are keyed off the URL
        // prefix, and /admin/platform* is intentionally a "both"-context
        // route (route-context.ts) so RouteGuard never bounces off it.
        // Without this, picking Personal here left the user stranded on
        // /admin/platform* with no nav link back out.
        //
        // Off-platform, no explicit navigation is needed here at all: any
        // route that genuinely requires personal context (e.g.
        // /security-centre) is classified `personal-only` in
        // route-context.ts, and RouteGuard's own bounce-on-context-change
        // effect handles the redirect automatically the instant activeOrgId
        // flips — the same mechanism /memories and /guardian-setup already
        // rely on. Forcing every off-platform switch to /dashboard here
        // (an earlier version of this fix) was wrong: it also yanked the
        // user off routes like /connections, which already re-render
        // correctly in place for the new context and were never broken.
        if (onPlatform) setLocation("/security-centre");
        setOpen(false);
      }}
      onPickOrg={(orgId, orgName) => {
        // See onPickPersonal above — same optimistic-switch fix.
        void switchToOrg(orgId, orgName).catch(() => {});
        // See onPickPersonal above — same stranding fix, and same reasoning
        // for why nothing else navigates: RouteGuard's bounce covers
        // personal-only/org-only routes; "both" routes are, by definition,
        // meant to keep rendering (just with different content) across a
        // context switch.
        if (onPlatform) setLocation("/dashboard");
        setOpen(false);
      }}
      onPickPlatform={() => {
        setLocation("/admin/platform");
        setOpen(false);
      }}
    />
  );

  if (compact) {
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          // Accessible name carries the CURRENT profile, not just the action —
          // a collapsed sidebar has no visible label, so "Switch organization"
          // alone left a screen-reader user as much in the dark as a sighted
          // one (the whole defect this change fixes).
          aria-label={`Switch profile — currently ${label}`}
          title={`Acting as ${label} — click to switch profile`}
          className="mx-auto flex h-10 w-10 items-center justify-center rounded-md border border-border-default bg-bg-elevated text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          {/* Per-kind glyph (person / building / console): the collapsed rail
              previously showed the SAME building icon for Personal, every org,
              and the Platform Console, so it signalled nothing about which
              profile was active. */}
          <ActiveIcon size={18} aria-hidden="true" />
        </button>
        {open && rect && createPortal(
          <div
            ref={popoverRef}
            style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, minWidth: 200, zIndex: 60 }}
          >
            {optionsList}
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        className="flex w-full items-center gap-2 rounded-md border border-border-default bg-bg-elevated px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-overlay transition-colors"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        // Explicit, and identical in shape to the compact trigger's: without it
        // the accessible name is assembled from the visible content and comes
        // out as "Acting as <profile>", which names the STATE but never the
        // action this button performs. One control, one naming convention.
        aria-label={`Switch profile — currently ${label}`}
        title={`Acting as ${label} — click to switch profile`}
      >
        <ActiveIcon size={16} className="shrink-0 text-text-tertiary" aria-hidden="true" />
        {/* Two lines: the "Acting as" eyebrow turns what read as an anonymous
            dropdown into a statement of current state, so the profile name is
            understood as WHERE YOU ARE rather than as a menu title. This is the
            ONLY "Acting as" label on this control — more-sheet.tsx used to add
            its own above the trigger, which now reads as a duplicate. */}
        <span className="min-w-0 flex-1">
          {/* The eyebrow's shared class string rather than <SectionEyebrow>
              itself: that component renders a <p>, which is not valid phrasing
              content inside a <button>. */}
          <span className={`block ${SECTION_EYEBROW_TEXT_CLASS}`}>Acting as</span>
          {/* Wraps (max two lines) rather than truncating: org names like
              "Walkthrough Test Org" are wider than the sidebar, and the name is
              the whole point of this control. The full name is in the title. */}
          <span className="line-clamp-2 break-words font-medium text-text-primary">{active.label}</span>
          {active.handle && (
            <span className="block truncate text-xs text-text-tertiary">{active.handle}</span>
          )}
        </span>
        <span aria-hidden className="shrink-0 text-text-tertiary">▾</span>
      </button>
      {open && (
        <div
          ref={popoverRef}
          className={`absolute left-0 right-0 z-10 ${openUpward ? "bottom-full mb-1" : "mt-1"}`}
        >
          {optionsList}
        </div>
      )}
    </div>
  );
}
